import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { libraryQuestionPath } from '@/constants/appRoutes';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { libraryQuestionsApi, type LibraryQuestion } from '@/services/api';

export function StaffQuestionsSection() {
  const [items, setItems] = useState<LibraryQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    libraryQuestionsApi
      .list({ channel: 'staff', status: 'open', limit: 50 })
      .then((data) => {
        setItems(data.items);
        setError(null);
      })
      .catch(() => setError('Could not load open staff questions.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (id: string) => {
    const body = drafts[id]?.trim();
    if (!body || savingId) return;
    setSavingId(id);
    setError(null);
    try {
      await libraryQuestionsApi.answer(id, body);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      load();
    } catch {
      setError('Could not save that answer.');
      setSavingId(null);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight text-primary">Open staff questions</h2>
          <p className="text-xs text-muted-foreground">
            Questions sent to the RCM team. An answer is published in the Library.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>
      <div className="px-4 py-3 space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner size="sm" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No open staff questions.</p>
        ) : (
          items.map((question) => (
            <div key={question.id} className="rounded-lg border border-border p-3 space-y-2">
              <p className="text-sm whitespace-pre-wrap">{question.body}</p>
              <p className="text-xs text-muted-foreground">
                {question.author_name || 'Member'} · {new Date(question.created_at).toLocaleString()}
                {' · '}
                <Link to={libraryQuestionPath(question.id)} className="font-medium text-[hsl(var(--accent-brand))] hover:underline">
                  Open in Library
                </Link>
              </p>
              <Textarea
                value={drafts[question.id] ?? ''}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [question.id]: e.target.value }))}
                rows={3}
                placeholder="Write the team answer…"
              />
              <Button
                size="sm"
                className="bg-primary text-primary-foreground shadow-none hover:bg-primary/90"
                disabled={savingId === question.id || !(drafts[question.id] ?? '').trim()}
                onClick={() => void submit(question.id)}
              >
                {savingId === question.id ? 'Saving…' : 'Post answer'}
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
